import { PayOutsProvider } from '~/contexts/pay-outs';
import { PaymentProviderBranch } from './PaymentProviderBranch';
import { AuthProvider } from './auth/AuthContext';
import { BankAccountsListProvider } from './bank-accounts';
import { ServiceBillProvider } from './bills';
import { ChartOfAccountsProvider } from './chart-of-accounts';
import { GlobalChatProvider } from './chats';
import { CompaniesProvider, CompanyInvoicesProvider } from './companies';
import { CompanyBusinessInfoProvider } from './company-business-info/BusinessInfoContext';
import { CustomLayoutProvider } from './custom-layout';
import { CustomViewsProvider } from './custom-views';
import { DepositSlipsProvider } from './deposit-slips';
import { EmailsProvider } from './emails';
import { FeatureHighlightsProvider } from './featureHighlights/FeatureHighlightsContext';
import { SignalRProvider } from './infrastructures';
import { InvoiceCreditsProvider } from './invoice-credits';
import { LeaseTemplateProvider, LeaseTemplateCategoryProvider } from './lease-templates';
import { AutomaticLeaseRenewalProvider } from './leases/AutomaticLeaseRenewalContext';
import { ManagementFeesSettingsProvider } from './local';
import { MagicTagsProvider } from './magic-tags';
import { ManagementFeeProvider } from './management-fee';
import { NotesProvider } from './notes';
import { OnboardingProvider } from './onboarding';
import { OwnersProvider } from './owners';
import { PropertyManagerPermissionsProvider } from './permissions/PropertyManagerPermissionsContext';
import { PortfoliosProvider } from './portfolios';
import { PropertiesProvider } from './properties';
import { PropertyCreditProvider } from './property-credits';
import { ReferralProvider } from './referral';
import { RentalApplicationProvider } from './rental-application';
import { RunBooksProvider } from './run-books';
import { CompanySettingsProvider } from './settings/CompanySettingsContext';
import { PropertyManagersProvider } from './settings/PropertyManagerSettingsContext';
import { SubscriptionProvider } from './subscription/SubscriptionContext';
import { SubsidizersProvider } from './subsidizers';
import { TenantsProvider } from './tenants';
import { TranslationsProvider } from './translations';
import { TrialStatusProvider } from './trial/TrialStatusContext';
import { UnitListingsProvider } from './unit-listings';
import { UnitsProvider } from './units';
import { VendorsProvider } from './vendors';
import { WorkOrdersProvider } from './work-orders';
import type { Component, JSX } from 'solid-js';

type Provider = Component<{ children: JSX.Element }>;

// global providers registry (stacked)
const providers: Provider[] = [
  SignalRProvider,
  CompanyBusinessInfoProvider,
  GlobalChatProvider,
  CompaniesProvider,
  PaymentProviderBranch,
  CompanyInvoicesProvider,
  CompanySettingsProvider,
  FeatureHighlightsProvider,
  SubscriptionProvider,
  PropertyManagersProvider,
  PropertyManagerPermissionsProvider,
  PortfoliosProvider,
  PropertiesProvider,
  UnitsProvider,
  NotesProvider,
  UnitListingsProvider,
  TenantsProvider,
  TranslationsProvider,
  BankAccountsListProvider,
  ChartOfAccountsProvider,
  VendorsProvider,
  RentalApplicationProvider,
  ManagementFeeProvider,
  PayOutsProvider,
  ManagementFeesSettingsProvider,
  CustomLayoutProvider,
  OwnersProvider,
  WorkOrdersProvider,
  DepositSlipsProvider,
  RunBooksProvider,
  EmailsProvider,
  PropertyCreditProvider,
  InvoiceCreditsProvider,
  LeaseTemplateCategoryProvider,
  LeaseTemplateProvider,
  OnboardingProvider,
  AutomaticLeaseRenewalProvider,
  MagicTagsProvider,
  CustomViewsProvider,
  SubsidizersProvider,
  TrialStatusProvider,
  ServiceBillProvider,
  ReferralProvider,
  // more global providers can be added here
];

export { AuthProvider, ChartOfAccountsProvider };

// export all global hooks
export { useSignalR } from './infrastructures';
export { useAuth, type Profile } from './auth/AuthContext';
export { useGlobalChat } from './chats';
export { useRentalApplication, useLeasingRentalApplication, LeasingRentalApplicationProvider } from './rental-application';
export { useCurrentCompany, useCompanyInvoices } from './companies';
export { usePortalPaymentAccounts } from './portal-payment-accounts';
export { useCompanySettings } from './settings/CompanySettingsContext';
export { useFeatureHighlights } from './featureHighlights/FeatureHighlightsContext';
export { usePortfolios } from './portfolios';
export { useProperties, type PropertyImage } from './properties';
export { useUnits } from './units';
export { useUnitListings } from './unit-listings';
export { useTenants } from './tenants';
export { useTranslations } from './translations';
export {
  useChartOfAccounts,
  chartOfAccountNameMap,
  findDefaultChartOfAccount,
  type ChartOfAccountGroup,
  type DefaultChartOfAccountType,
} from './chart-of-accounts';
export { useVendors } from './vendors';
export { useSubsidizers } from './subsidizers';
export { useManagementFee } from './management-fee';
export { usePayOuts } from './pay-outs';
export { usePayPaymentMethods } from './pay';
export { useManagementFeesSettings } from './local';
export { useCustomLayout } from './custom-layout';
export { useOwners } from './owners';
export { useWorkOrders } from './work-orders';
export { useDepositSlips } from './deposit-slips';
export { useEmails } from './emails';
export { useNotes } from './notes';
export { usePropertyCredit } from './property-credits';
export { useInvoiceCredits } from './invoice-credits';
export { createMagicDoorContext } from './utils';
export { useOnboarding } from './onboarding';
export { useCompanyBusinessInfo } from './company-business-info/BusinessInfoContext';
export { useAutomaticLeaseRenewal } from './leases/AutomaticLeaseRenewalContext';
export { useMagicTags } from './magic-tags';
export { useCustomViews, defaultViewName } from './custom-views';
export { useBankAccountsList } from './bank-accounts';
export { useTrialStatus } from './trial/TrialStatusContext';
export { usePropertyManagerPermissions } from './permissions/PropertyManagerPermissionsContext';
export { usePropertyManagers } from './settings/PropertyManagerSettingsContext';
export { useServiceBill } from './bills';
export { useReferral } from './referral';
export { useSubscription } from './subscription/SubscriptionContext';

export type { MixedPayOutBankAccountDto } from './pay-outs';
export const GlobalProviders: Provider = (props) => {
  return providers.reduceRight(
    (acc, provider) => ({
      get children() {
        return provider(acc);
      },
    }),
    {
      get children() {
        return props.children;
      },
    }
  ).children;
};
